'use client'

import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { HexColorPicker } from 'react-colorful'
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react'

import { cn } from '@/lib/utils'
import { onColor } from '@/lib/color/contrast'
import { DEFAULT_VCARD_FORM_VALUES } from '@/constants/VCard'
import Button from '@/components/livecontact/Button'
import IconButton from '@/components/livecontact/IconButton'
import Input from '@/components/livecontact/Input'
import LcIcon from '@/components/livecontact/icons'
import { POP_CLASS } from '@/components/livecontact/fieldStyles'

type AnchorSide = 'bottom start' | 'bottom end' | 'top start' | 'top end'

type Props = {
  value: string
  onChange: (value: string) => void
  onBlur?: () => void
  className?: string
  disabled?: boolean
  /** Quick swatches under the picker; the matching one shows a ring + check. */
  presets?: string[]
  anchor?: AnchorSide
}

type EyeDropperCtor = new () => { open: () => Promise<{ sRGBHex: string }> }

const ORIGIN: Record<AnchorSide, string> = {
  'bottom start': 'origin-top-left',
  'bottom end': 'origin-top-right',
  'top start': 'origin-bottom-left',
  'top end': 'origin-bottom-right',
}

/** Checkerboard shown when no colour is set. */
const EMPTY_PATTERN =
  'bg-surface-2 bg-[repeating-linear-gradient(45deg,rgb(var(--lc-line-2))_0,rgb(var(--lc-line-2))_1px,transparent_0,transparent_50%)] bg-[length:8px_8px]'

/** react-colorful restyled: soft-square saturation area, slim rounded hue bar, 16px pointers. */
const COLORFUL_CLASS =
  '[&_.react-colorful]:h-[168px] [&_.react-colorful]:w-full [&_.react-colorful]:cursor-crosshair ' +
  '[&_.react-colorful>div:first-child]:rounded-[8px] [&_.react-colorful>div:first-child]:border-b-0 ' +
  '[&_.react-colorful>div:last-child]:mt-3 [&_.react-colorful>div:last-child]:h-2.5 [&_.react-colorful>div:last-child]:rounded-full ' +
  '[&_.react-colorful>div>div>div]:size-4 [&_.react-colorful>div>div>div]:border-2'

const ColorPicker = forwardRef<HTMLButtonElement, Props>(
  (
    { disabled, value, onChange, onBlur, className, presets, anchor = 'bottom start', ...props },
    ref
  ) => {
    const triggerRef = useRef<HTMLButtonElement>(null)
    const [hasEyeDropper, setHasEyeDropper] = useState(false)
    const isEmpty = !value
    const parsedValue = useMemo(() => value || '#FFFFFF', [value])

    useEffect(() => {
      setHasEyeDropper(typeof window !== 'undefined' && 'EyeDropper' in window)
    }, [])

    const handleRef = useCallback(
      (node: HTMLButtonElement | null) => {
        ;(triggerRef as React.MutableRefObject<HTMLButtonElement | null>).current = node
        if (typeof ref === 'function') ref(node)
        else if (ref) (ref as React.MutableRefObject<HTMLButtonElement | null>).current = node
      },
      [ref]
    )

    const handleHexInput = useCallback(
      (raw: string) => {
        const hex = raw.replace(/[^0-9a-fA-F]/g, '').slice(0, 6)
        onChange(hex ? `#${hex.toUpperCase()}` : '')
      },
      [onChange]
    )

    const pickFromScreen = useCallback(async () => {
      try {
        const Ctor = (window as unknown as { EyeDropper?: EyeDropperCtor }).EyeDropper
        if (!Ctor) return
        const result = await new Ctor().open()
        if (result?.sRGBHex) onChange(result.sRGBHex.toUpperCase())
      } catch {
        /* user dismissed the dropper */
      }
    }, [onChange])

    return (
      <Popover className={cn('relative inline-flex', className?.includes('w-full') && 'w-full')}>
        <PopoverButton
          ref={handleRef}
          disabled={disabled}
          onBlur={onBlur}
          aria-label={isEmpty ? 'Pick a colour' : `Pick a colour, currently ${value}`}
          title={isEmpty ? 'Pick a colour' : value}
          className={cn(
            'relative h-8 w-8 shrink-0 cursor-pointer overflow-hidden rounded-[8px] outline-none',
            'shadow-[inset_0_0_0_1px_rgba(0,0,0,0.08)] dark:shadow-[inset_0_0_0_1px_rgba(255,255,255,0.12)]',
            'transition-[transform,box-shadow] duration-[160ms] ease-out-strong active:scale-[0.94]',
            'focus-visible:ring-2 focus-visible:ring-brand/40 data-[open]:ring-2 data-[open]:ring-brand/40',
            'disabled:cursor-not-allowed disabled:opacity-50 disabled:active:scale-100',
            isEmpty && EMPTY_PATTERN,
            className
          )}
          style={isEmpty ? undefined : { backgroundColor: parsedValue }}
          {...props}
        />

        <PopoverPanel
          transition
          anchor={{ to: anchor, gap: 6, padding: 8 }}
          className={cn(
            POP_CLASS,
            'z-[60] w-[240px] p-3',
            ORIGIN[anchor],
            'transition duration-[180ms] ease-out-strong data-[closed]:scale-[0.96] data-[closed]:opacity-0 data-[closed]:duration-[120ms]'
          )}
        >
          <div className={cn('flex flex-col gap-3', COLORFUL_CLASS)}>
            <HexColorPicker color={parsedValue} onChange={(next) => onChange(next.toUpperCase())} />

            <div className="flex items-center gap-2">
              <Input
                value={value}
                onChange={(e) => handleHexInput(e.target.value)}
                placeholder="#000000"
                maxLength={7}
                spellCheck={false}
                autoComplete="off"
                aria-label="Hex colour"
                classInput="font-medium uppercase tabular-nums"
                leading={
                  <span
                    className={cn(
                      'size-4 rounded-[4px] shadow-[inset_0_0_0_1px_rgba(0,0,0,0.08)]',
                      isEmpty && EMPTY_PATTERN
                    )}
                    style={isEmpty ? undefined : { backgroundColor: parsedValue }}
                  />
                }
              />
              {hasEyeDropper && (
                <IconButton
                  label="Pick from screen"
                  size="sm"
                  shape="square"
                  tone="muted"
                  onClick={pickFromScreen}
                >
                  <LcIcon name="pipette" />
                </IconButton>
              )}
            </div>

            {presets && presets.length > 0 && (
              <div className="flex flex-wrap gap-2" role="group" aria-label="Preset colours">
                {presets.map((preset) => {
                  const selected = !isEmpty && value.toLowerCase() === preset.toLowerCase()
                  return (
                    <button
                      key={preset}
                      type="button"
                      aria-label={preset}
                      aria-pressed={selected}
                      title={preset}
                      onClick={() => onChange(preset.toUpperCase())}
                      style={{ backgroundColor: preset }}
                      className={cn(
                        'inline-flex size-6 items-center justify-center rounded-full shadow-[inset_0_0_0_1px_rgba(0,0,0,0.08)] outline-none',
                        'transition-[transform,box-shadow] duration-[160ms] ease-out-strong active:scale-[0.92] focus-visible:ring-2 focus-visible:ring-brand/40',
                        selected &&
                          'ring-2 ring-ink ring-offset-2 ring-offset-surface dark:ring-offset-surface-2'
                      )}
                    >
                      {selected && (
                        <LcIcon
                          name="check"
                          size={12}
                          strokeWidth={2.5}
                          className="animate-lc-pop"
                          style={{ color: onColor(preset) }}
                        />
                      )}
                    </button>
                  )
                })}
              </div>
            )}

            {!isEmpty && (
              <Button
                variant="ghost"
                size="sm"
                className="w-full text-ink-3"
                leadingIcon={<LcIcon name="refresh" />}
                onClick={() => onChange(DEFAULT_VCARD_FORM_VALUES.mainColor)}
              >
                Reset to default
              </Button>
            )}
          </div>
        </PopoverPanel>
      </Popover>
    )
  }
)
ColorPicker.displayName = 'ColorPicker'

export { ColorPicker }
